import { z } from 'zod';
import { defineManifest } from '@dashboard/module-contract/manifest';
import type { RolesOf } from '@dashboard/module-contract/manifest';

/**
 * Static websites in the client's account.
 *
 * **The object is a site, not its parts** (`D33`): nobody here is asked to
 * assemble a bucket, an origin access control, a distribution, a certificate,
 * a cache policy and a set of DNS records. The trade is flexibility — a site
 * needing something this object cannot express cannot be built here, and the
 * module says so rather than quietly building something adjacent.
 *
 * **This is the first slice.** Sites are listed and nothing creates one yet;
 * see `D110` for what is deliberately absent and why nothing is declared ahead
 * of being used.
 */

export const settings = z.object({
	/**
	 * How far CloudFront serves a new site from, by default.
	 *
	 * Per site in the end — it is coverage against cost, and the answer differs
	 * for a brochure site and a product used from three continents — but a
	 * deployment has a usual answer and this is it. `PriceClass_100` is North
	 * America and Europe, which is the cheapest and the common case.
	 */
	defaultPriceClass: z
		.enum(['PriceClass_100', 'PriceClass_200', 'PriceClass_All'])
		.default('PriceClass_100'),
});

export type SitesSettings = z.infer<typeof settings>;

export const manifest = defineManifest({
	id: 'sites',
	title: 'Websites',
	/**
	 * Three, because the actions differ in kind rather than in degree.
	 * Uploading a file changes what a site serves; creating one provisions
	 * billable infrastructure and deleting one destroys content.
	 *
	 * `operator` and `admin` are declared now and confer only what is built —
	 * which today is reading. They are not speculative: `admin` already
	 * governs the settings page below, the way `D109` asks.
	 */
	roles: [
		{ name: 'reader', title: 'Reader', description: 'View sites and their status' },
		{
			name: 'operator',
			title: 'Operator',
			description: 'Upload content, and set routing and password',
			includes: ['reader'],
		},
		{
			name: 'admin',
			title: 'Administrator',
			description: 'Create and delete sites',
			includes: ['operator'],
		},
	],
	nav: [
		{ path: '', title: 'Sites', requiredRole: 'sites:reader' },
		{ path: 'settings', title: 'Settings', requiredRole: 'sites:admin' },
	],
	settings,
});

/** Declared once, in the manifest. Every other surface derives from it. */
export type SitesRole = RolesOf<typeof manifest>;
